import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppSelector, useAppDispatch } from "../redux/hooks";
import {
  selectCurrentAccessToken,
  selectCurrentRefreshToken,
  setCredentials,
} from "../redux/auth/authSlice";
import {
  selectEnableSessionTimeout,
  selectExpiryTimeInMinute,
  selectWarningAlertTimeoutInMinute,
} from "../redux/config/configSlice";
import { useRefreshSessionMutation } from "../redux/auth/authApiSlice";
import useAuth from "./useAuth";

interface UseIdleTimeoutReturn {
  showWarning: boolean;
  remainingTime: number;
  resetTimer: () => void;
  handleLogout: () => void;
  isSessionTimeoutEnabled: boolean;
}

const WARNING_TIME_SEC = 30; // 30 seconds warning before session expiry

const useIdleTimeout = (): UseIdleTimeoutReturn => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { logout } = useAuth();

  const jwtToken = useAppSelector(selectCurrentAccessToken);
  const refreshTokenValue = useAppSelector(selectCurrentRefreshToken);
  const isSessionTimeoutEnabled = useAppSelector(selectEnableSessionTimeout);
  const expiryTimeMin = useAppSelector(selectExpiryTimeInMinute);
  const warningTimeMin = useAppSelector(selectWarningAlertTimeoutInMinute);

  const expiryTimeSec = expiryTimeMin * 60;
  const warningTimeSec = warningTimeMin * 60 || WARNING_TIME_SEC;

  const [showWarning, setShowWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(warningTimeSec);
  const [sessionKey, setSessionKey] = useState(0);

  const [refreshSessionApi] = useRefreshSessionMutation();

  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasLoggedOutRef = useRef(false);

  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const handleLogout = useCallback(async () => {
    if (hasLoggedOutRef.current) return;
    hasLoggedOutRef.current = true;

    clearAllTimers();
    setShowWarning(false);

    toast.dismiss();
    toast.error("Your session has expired due to inactivity. Please login again.");
    await logout({ isSessionTimeout: true });
    navigate("/login", { replace: true });
  }, [clearAllTimers, logout, navigate]);

  // "Stay Logged In" - refresh the token and restart the session timer
  const resetTimer = useCallback(async () => {
    clearAllTimers();
    setShowWarning(false);
    setRemainingTime(warningTimeSec);

    try {
      const response: any = await refreshSessionApi({
        accessToken: jwtToken,
        refreshToken: refreshTokenValue,
      }).unwrap();
      dispatch(setCredentials(response));
      // Increment sessionKey to re-trigger the timer useEffect
      setSessionKey((prev) => prev + 1);
    } catch {
      // If refresh fails, force logout
      handleLogout();
    }
  }, [clearAllTimers, warningTimeSec, refreshSessionApi, jwtToken, refreshTokenValue, dispatch, handleLogout]);

  // Main effect: schedule warning and logout based on config expiryTimeInMinute
  useEffect(() => {
    if (!jwtToken || !isSessionTimeoutEnabled) return;

    clearAllTimers();
    hasLoggedOutRef.current = false;
    setShowWarning(false);
    setRemainingTime(warningTimeSec);

    const timeUntilWarningSec = expiryTimeSec - warningTimeSec;

    const startCountdown = (countdownSec: number) => {
      setShowWarning(true);
      setRemainingTime(Math.round(countdownSec));

      const logoutTime = Date.now() + countdownSec * 1000;

      countdownIntervalRef.current = setInterval(() => {
        const remaining = Math.max(0, Math.round((logoutTime - Date.now()) / 1000));
        setRemainingTime(remaining);
        if (remaining <= 0 && countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
        }
      }, 1000);

      logoutTimerRef.current = setTimeout(() => {
        handleLogout();
      }, countdownSec * 1000);
    };

    // If expiry time is less than or equal to warning time, show warning immediately
    if (timeUntilWarningSec <= 0) {
      startCountdown(expiryTimeSec);
      return;
    }

    // Schedule warning before session expiry
    warningTimerRef.current = setTimeout(() => {
      startCountdown(warningTimeSec);
    }, timeUntilWarningSec * 1000);

    return () => {
      clearAllTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwtToken, expiryTimeSec, warningTimeSec, isSessionTimeoutEnabled, sessionKey]);

  return {
    showWarning,
    remainingTime,
    resetTimer,
    handleLogout,
    isSessionTimeoutEnabled,
  };
};

export default useIdleTimeout;
